import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { computed, type Ref } from 'vue'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import type { WaitlistEnrollmentInputArray } from '~/composables/wait-list-enrollment-input'
import type { WaitlistedCustomerConstraintNewCustomerInput } from '~/resources/graphql/graphql'

export const WaitlistedCustomerConstraintPlanContractFragment = graphql(`
    fragment WaitlistedCustomerConstraintPlanContractFragment on Course {
        id
        waitlistConstraint {
            ...WaitlistedCustomerConstraintWaitlistedCustomerConstraintsFragment
        }
    }
`)

const WaitlistedCustomerConstraintWaitlistedCustomerConstraintsFragment =
    graphql(`
        fragment WaitlistedCustomerConstraintWaitlistedCustomerConstraintsFragment on WaitlistedCustomerConstraints {
            constraintEnforced
            constraintTranslatedMessage(language: ENGLISH)
            results {
                customer {
                    __typename
                    ... on Customer {
                        id
                    }
                    ... on ConstraintNewCustomer {
                        key
                    }
                }
                valid
            }
        }
    `)

export function useWaitlistedCustomerConstraint(
    waitlistedCustomerInput: Ref<WaitlistEnrollmentInputArray>,
    course: Ref<
        FragmentType<typeof WaitlistedCustomerConstraintPlanContractFragment>
    >
) {
    const courseData = computed(() =>
        getFragmentData(
            WaitlistedCustomerConstraintPlanContractFragment,
            course.value
        )
    )

    const newCustomerFilter = computed(() => {
        return waitlistedCustomerInput.value
            .map(waitlistedCustomer => {
                if (
                    (waitlistedCustomer.invalid &&
                        !waitlistedCustomer.invalidFromConstraint) ||
                    waitlistedCustomer.data === null ||
                    waitlistedCustomer.data.type === 'existing'
                ) {
                    return null
                }

                return {
                    key: waitlistedCustomer.data.key,
                    firstName: waitlistedCustomer.data.firstName,
                    middleName: waitlistedCustomer.data.middleName,
                    lastName: waitlistedCustomer.data.lastName,
                    dateOfBirth: waitlistedCustomer.data.dateOfBirth
                }
            })
            .filter(
                value => value !== null
            ) as WaitlistedCustomerConstraintNewCustomerInput[]
    })

    const rpQuery = useRpQuery()
    const { data } = useQuery({
        queryKey: [
            'WaitlistedCustomerNewCustomerConstraintQuery',
            newCustomerFilter
        ],
        queryFn: async () => {
            if (newCustomerFilter.value.length === 0) {
                return null
            }

            const response = await rpQuery(
                graphql(`
                    query WaitlistedCustomerNewCustomerConstraintQuery(
                        $id: ID!
                        $filter: WaitlistedCustomerConstraintNewCustomerFilter!
                    ) {
                        node(id: $id) {
                            __typename
                            ... on Course {
                                waitlistConstraintNewCustomers(
                                    filter: $filter
                                ) {
                                    ...WaitlistedCustomerConstraintWaitlistedCustomerConstraintsFragment
                                }
                            }
                        }
                    }
                `),
                {
                    id: courseData.value.id,
                    filter: {
                        customers: newCustomerFilter.value
                    }
                }
            )

            if (
                response.node !== null &&
                response.node.__typename === 'Course' &&
                response.node.waitlistConstraintNewCustomers
            ) {
                return response.node.waitlistConstraintNewCustomers
            }

            return null
        }
    })

    const combinedWaitlistedCustomerConstraints = computed(() => {
        if (courseData.value.waitlistConstraint === null) {
            return null
        }

        const constraint = getFragmentData(
            WaitlistedCustomerConstraintWaitlistedCustomerConstraintsFragment,
            courseData.value.waitlistConstraint
        )

        if (data.value !== undefined && data.value !== null) {
            const dataConstraint = getFragmentData(
                WaitlistedCustomerConstraintWaitlistedCustomerConstraintsFragment,
                data.value
            )

            return {
                constraintEnforced: constraint.constraintEnforced,
                constraintTranslatedMessage:
                    constraint.constraintTranslatedMessage,
                results: [...constraint.results, ...dataConstraint.results]
            }
        } else {
            return constraint
        }
    })

    const waitlistedCustomerConstraintSegmentLookup = computed(() => {
        const map = new Map<
            string,
            {
                valid: boolean
                required: boolean
                message: string
            }
        >()

        const constraint = combinedWaitlistedCustomerConstraints.value
        if (constraint === null) {
            return map
        }

        constraint.results.forEach(result => {
            // Skip anybody who does not have a customer segment to match on
            if (result.valid === null) {
                return
            }

            const key =
                result.customer.__typename === 'Customer'
                    ? result.customer.id
                    : result.customer.key

            map.set(key, {
                valid: result.valid,
                required: constraint.constraintEnforced,
                message: constraint.constraintTranslatedMessage
            })
        })

        return map
    })

    return {
        waitlistedCustomerConstraintSegmentLookup
    }
}

export type WaitlistedCustomerConstraints = ReturnType<
    typeof useWaitlistedCustomerConstraint
>
